import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Shield, Landmark, User, Briefcase, UserCog, ArrowRight, Building2, Languages } from "lucide-react";
import RajasthanMap from "@/components/common/RajasthanMap";
import TestimonialsSection from "@/components/site/TestimonialsSection";
import ContactCTASection from "@/components/site/ContactCTASection";
import SiteFooter from "@/components/site/SiteFooter";

const HERO_IMG =
  "https://images.unsplash.com/photo-1477587458883-47145ed94245?crop=entropy&cs=srgb&fm=jpg&auto=format&fit=crop&w=2400&q=80";
// Hawa Mahal / Rajasthani palace facade — pan-Rajasthan heritage aesthetic

const ROLES = [
  { role: "admin", icon: Shield, color: "text-blue-600" },
  { role: "manager", icon: UserCog, color: "text-rose-600" },
  { role: "bank", icon: Landmark, color: "text-emerald-600" },
  { role: "field", icon: User, color: "text-amber-600" },
  { role: "office", icon: Briefcase, color: "text-violet-600" },
];

export default function Landing() {
  const { t, lang, toggle } = useLang();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Nav */}
      <header className="glass-panel sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
            <div className="h-9 w-9 rounded-md bg-slate-950 text-white flex items-center justify-center">
              <Building2 className="h-5 w-5" />
            </div>
            <div className="leading-tight">
              <div className="font-heading font-bold text-slate-950">{t("app_name")}</div>
              <div className="text-xs text-slate-500">Pan Rajasthan · Property Valuation</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to="/training"
              className="text-sm text-slate-700 hover:text-slate-950 hidden sm:inline-flex px-3 py-2 rounded-md hover:bg-slate-100"
              data-testid="nav-training"
            >
              {t("training_courses")}
            </Link>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggle}
              className="gap-2"
              data-testid="lang-toggle"
            >
              <Languages className="h-4 w-4" />
              {lang === "en" ? "हिन्दी" : "EN"}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/85 via-slate-950/75 to-slate-900/70" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div className="max-w-3xl">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-300 mb-4">
                B2B · Pan-Rajasthan · 33 Districts
              </p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.05]">
                {t("landing_hero")}
              </h1>
              <p className="mt-6 text-base sm:text-lg text-slate-200 max-w-2xl leading-relaxed">
                {t("landing_sub")}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="#roles">
                  <Button
                    size="lg"
                    className="bg-amber-600 hover:bg-amber-700 text-white rounded-md"
                    data-testid="cta-signin"
                  >
                    {t("sign_in")} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
                <Link to="/training">
                  <Button
                    size="lg"
                    variant="secondary"
                    className="bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-md"
                    data-testid="cta-training"
                  >
                    {t("training_courses")}
                  </Button>
                </Link>
              </div>
            </div>

            <div className="hidden lg:flex justify-center items-center">
              <div className="relative w-full max-w-md">
                <div className="absolute inset-0 bg-amber-500/10 rounded-full blur-3xl" aria-hidden="true" />
                <RajasthanMap className="relative" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Roles */}
      <section id="roles" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
            {t("choose_role")}
          </p>
          <h2 className="mt-2 font-heading text-2xl sm:text-3xl font-semibold text-slate-950">
            Five dashboards. One valuation engine.
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6">
          {ROLES.map(({ role, icon: Icon, color }) => (
            <div
              key={role}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/login/${role}`)}
              onKeyDown={(e) => { if (e.key === "Enter") navigate(`/login/${role}`); }}
              data-testid={`role-card-${role}`}
              className="group cursor-pointer bg-white border border-slate-200 rounded-lg p-6 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:border-slate-300"
            >
              <div className={`h-10 w-10 rounded-md bg-slate-50 border border-slate-200 flex items-center justify-center ${color} mb-6`}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">
                {String(role).toUpperCase()}
              </div>
              <h3 className="font-heading text-xl font-semibold text-slate-950 mb-3">{t(role)}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                {role === "admin" && "Full platform control — banks, leads, executives, reports."}
                {role === "manager" && "Approve executives, manage leads within your assigned pincodes."}
                {role === "bank" && "Generate leads, track TAT, submit valuation reports."}
                {role === "field" && "Visit properties, upload photos & bylaws, track earnings."}
                {role === "office" && "Assign, coordinate, submit valuations to bank portals."}
              </p>
              <div className="mt-6 flex items-center justify-between gap-2">
                <div className="text-sm font-medium text-blue-600 flex items-center gap-1 group-hover:gap-2 transition-all">
                  {t("login")} <ArrowRight className="h-4 w-4" />
                </div>
                {(role === "bank" || role === "field") && (
                  <Link
                    to={`/signup/${role}`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-xs text-slate-500 hover:text-slate-900 hover:underline"
                    data-testid={`role-signup-${role}`}
                  >
                    Sign up
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Feature strip */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { k: "33+", v: "Districts covered" },
            { k: "24-48 hr", v: "Turnaround time" },
            { k: "Market rate", v: "Per-report fee, as per market standard" },
            { k: "100%", v: "Digital audit trail" },
          ].map((x) => (
            <div key={x.v}>
              <div className="num text-3xl sm:text-4xl font-bold text-slate-950">{x.k}</div>
              <div className="text-sm text-slate-500 mt-1">{x.v}</div>
            </div>
          ))}
        </div>
      </section>

      <TestimonialsSection />
      <ContactCTASection />
      <SiteFooter />
    </div>
  );
}
